import { Popover, PopoverTrigger, HStack, Spacer, Icon, PopoverContent, PopoverBody, Box, Text, PopoverAnchor } from "@chakra-ui/react"
import numeral from "numeral"
import { AiOutlineInfoCircle } from "react-icons/ai"
interface PlanDetailsItemProps {
  inclusion: number
  overage: number
  unit: number
  helpText?: React.ReactNode
  showCheck?: boolean
  padding?: string
}

export const CustomCheckIcon = () => {
  return <img src="https://ik.imagekit.io/ikmedia/icons-for-feature-table-check.svg" height={"20px"} width={"20px"} />
}

const CellContent = ({ children, isOpen, padding }) => {
  return (
    <HStack alignItems={"flex-start"} spacing={3} w={"full"} bg={isOpen ? "gray.50" : "transparent"} p={padding} fontFamily={"Inter"} fontSize={"14px"}>
      {children}
      {isOpen !== false && <Icon visibility={isOpen ? 'visible' : 'hidden'} alignSelf={"center"} as={AiOutlineInfoCircle} color="#888" style={{ cursor: "help" }} h={"20px"} w={"20px"} />}
    </HStack>
  )
}

export const HelpPopover = ({ children, helpText, padding }: { children: React.ReactNode, helpText?: React.ReactNode, padding: string }) => {
  if (!helpText) {
    return <CellContent isOpen={false} padding={padding}>{children}</CellContent>
  }
  return (
    <Popover autoFocus={false} isOpen={!helpText ? false : undefined} trigger="hover" placement={"right"} offset={[0, -15]} openDelay={0} closeDelay={0}>
      {({ isOpen }) => (
        <>
          <PopoverTrigger>
            {CellContent({ children, isOpen, padding })}
          </PopoverTrigger>
          <PopoverContent>
            <PopoverBody boxShadow={"lg"} p={"10px 20px"}>
              {helpText}
            </PopoverBody>
          </PopoverContent>
        </>
      )}
    </Popover>
  )
}

export const PurgeCacheItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion}</Text>
        <Text as="span" color={"imagekitGray.700"}> purge requests</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Monthly limit"
            : `$${overage}/${unit > 1 ? numeral(unit).format('0A').toUpperCase() + ' ' : ''}requests after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}

export const ExtensionItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion}</Text>
        <Text as="span" color={"imagekitGray.700"}> extension units</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Monthly limit"
            : `$${overage}/${unit > 1 ? numeral(unit).format('0A').toUpperCase() + ' ' : ''}units after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}

export const VPUItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion}</Text>
        <Text as="span" color={"imagekitGray.700"}> video units</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Monthly limit"
            : `$${overage}/${unit > 1 ? numeral(unit).format('0A').toUpperCase() + ' ' : ''}units after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}

export const StorageItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion} GB</Text>
        <Text as="span" color={"imagekitGray.700"}> DAM storage</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Fixed limit"
            : `$${overage}/${unit > 1 ? unit + ' ' : ''}GB after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}

export const CNAMEItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion}</Text>
        <Text as="span" color={"imagekitGray.700"}> included</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Fixed limit"
            : `$${overage}/${unit > 1 ? unit + ' ' : ''}domain after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}


export const BandwidthItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion} GB</Text>
        <Text as="span" color={"imagekitGray.700"}> bandwidth</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Monthly limit"
            : `$${overage}/${unit > 1 ? unit + ' ' : ''}GB after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}

export const UserItem = ({ inclusion, overage, unit, helpText, showCheck = true, padding = "8px 16px" }: PlanDetailsItemProps) => {
  return (
    <HelpPopover helpText={helpText} padding={padding}>
      {showCheck && <CustomCheckIcon />}
      <Box w={"full"}>
        <Text as="span" fontWeight="600" color={"imagekitGray.900"}>{inclusion}</Text>
        <Text as="span" color={"imagekitGray.700"}> users</Text>

        <Text color={"imagekitGray.600"} whiteSpace={helpText ? "nowrap": "normal"}>
          {overage === 0
            ? "Fixed limit"
            : `$${overage}/${unit > 1 ? numeral(unit).format('0A').toUpperCase() + ' ' : ''}user after that`}
        </Text>
      </Box>
    </HelpPopover>
  )
}